// src/pages/Employee/CreateEmployee/steps/Step3Address.tsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Row, Col, FormGroup, Label, Input, FormFeedback, Spinner } from 'reactstrap';
import { AddressData, StepProps } from '../../types/employee.types';
import FileUploadField from './fileuploadfile';
import { apiClient } from '../../helpers/api_helper';

const Step3Address: React.FC<StepProps<AddressData>> = ({ data, onChange, errors }) => {
  const [loadingPincode, setLoadingPincode] = useState(false);
  const [pincodeError, setPincodeError] = useState<string | null>(null);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const currentPincodeRef = useRef<string>(''); // Track current pincode being fetched

  const set = (field: keyof AddressData, value: any) => {
    onChange({ ...data, [field]: value });
  };

  // Fetch pincode details from API
  const fetchPincodeDetails = useCallback(async (pincode: string) => {
    if (!pincode || pincode.length !== 6) {
      setPincodeError(null);
      return;
    }

    // Store the pincode being fetched
    currentPincodeRef.current = pincode;

    setLoadingPincode(true);
    setPincodeError(null);

    try {
      const response = await apiClient.get(`/pincode/${pincode}`);
      const result = response;
      
      if (result.success && result.data) {
        const { state_name, district_name } = result.data;
        
        // Update state and district for present address
        // IMPORTANT: Keep the pincode unchanged
        const updates: any = {
          present_pincode: pincode, // Explicitly keep the pincode
        };
        
        if (state_name) {
          updates.present_state = state_name;
        }
        if (district_name) {
          updates.present_district = district_name;
        }
        
        // Apply all updates at once - preserve pincode
        onChange({ ...data, ...updates });
        setPincodeError(null);
      } else {
        setPincodeError('No data found for this pincode');
        // Clear state and district but KEEP the pincode
        onChange({ 
          ...data, 
          present_pincode: pincode, // Keep the pincode
          present_state: '', 
          present_district: '' 
        });
      }
    } catch (error: any) {
      console.error('Error fetching pincode details:', error);
      setPincodeError(error?.response?.data?.message || 'Failed to fetch pincode details');
      // Clear state and district but KEEP the pincode
      onChange({ 
        ...data, 
        present_pincode: pincode, // Keep the pincode
        present_state: '', 
        present_district: '' 
      });
    } finally {
      setLoadingPincode(false);
      currentPincodeRef.current = '';
    }
  }, [data, onChange]);

  // Handle pincode change with debouncing
  const handlePincodeChange = (value: string) => {
    // Only allow numbers and limit to 6 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 6);
    
    // Clear previous timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Update pincode immediately
    onChange({ ...data, present_pincode: numericValue });

    // Reset error and clear state/district if pincode is incomplete
    if (numericValue.length < 6) {
      setPincodeError(null);
      onChange({ 
        ...data, 
        present_pincode: numericValue,
        present_state: '', 
        present_district: '' 
      });
      return;
    }

    // Set timer for API call (500ms delay)
    const timer = setTimeout(() => {
      fetchPincodeDetails(numericValue);
    }, 500);
    setDebounceTimer(timer);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  // Toggle same as present address
  const toggleSameAsPresent = (checked: boolean) => {
    if (checked) {
      onChange({
        ...data,
        permanent_same_as_present: true,
        permanent_door_no: data.present_door_no,
        permanent_street: data.present_street,
        permanent_village: data.present_village,
        permanent_taluk: data.present_taluk,
        permanent_district: data.present_district,
        permanent_state: data.present_state,
        permanent_pincode: data.present_pincode,
      });
    } else {
      onChange({ ...data, permanent_same_as_present: false });
    }
  };

  const presentField = (
    key: keyof AddressData,
    label: string,
    colMd = 4,
    disabled = false
  ) => (
    <Col md={colMd}>
      <FormGroup>
        <Label className="fw-bold text-muted small text-uppercase">{label}</Label>
        <Input 
          value={data[key] as string || ''} 
          onChange={(e) => set(key, e.target.value)}
          disabled={disabled}
        />
      </FormGroup>
    </Col>
  );

  const permanentField = (
    key: keyof AddressData,
    label: string,
    colMd = 4,
  ) => (
    <Col md={colMd}>
      <FormGroup>
        <Label className="fw-bold text-muted small text-uppercase">{label}</Label>
        <Input
          value={data[key] as string || ''}
          disabled={data.permanent_same_as_present}
          onChange={(e) => set(key, e.target.value)}
        />
      </FormGroup>
    </Col>
  );

  return (
    <div>
      <h6 className="mb-3">Present Address</h6>
      
      {/* Door No, Street, Village, Taluk - In order */}
      <Row>
        {presentField('present_door_no', 'Door No')}
        {presentField('present_street', 'Street')}
        {presentField('present_village', 'Village / Town')}
      </Row>
      <Row>
        {presentField('present_taluk', 'Taluk')}
        
        {/* Pincode Field */}
        <Col md={4}>
          <FormGroup>
            <Label className="fw-bold text-muted small text-uppercase">
              Pincode
              {loadingPincode && <Spinner size="sm" className="ms-2" />}
            </Label>
            <Input
              value={data.present_pincode || ''}
              invalid={!!errors.present_pincode || !!pincodeError}
              maxLength={6}
              onChange={(e) => handlePincodeChange(e.target.value)}
              placeholder="Enter 6-digit pincode"
            />
            {loadingPincode && (
              <div className="text-muted small mt-1">
                <Spinner size="sm" className="me-1" />
                Fetching location details...
              </div>
            )}
            {pincodeError && (
              <div className="text-danger small mt-1">
                ✗ {pincodeError}
              </div>
            )}
            {!loadingPincode && data.present_pincode?.length === 6 && !pincodeError && data.present_state && (
              <div className="text-success small mt-1">
                ✓ Location found
              </div>
            )}
            <FormFeedback>{errors.present_pincode}</FormFeedback>
          </FormGroup>
        </Col>
      </Row>

      {/* State and District - Auto-filled, Read-only */}
      <Row>
        {presentField('present_state', 'State', 4, true)}
        {presentField('present_district', 'District', 4, true)}
      </Row>

      <div className="border-top pt-3 mt-2">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">Permanent Address</h6>
          <FormGroup check className="mb-0">
            <Input
              type="checkbox"
              id="sameAsPresent"
              checked={data.permanent_same_as_present || false}
              onChange={(e) => toggleSameAsPresent(e.target.checked)}
            />
            <Label check htmlFor="sameAsPresent" className="small text-muted">
              Same as present address
            </Label>
          </FormGroup>
        </div>

        <Row>
          {permanentField('permanent_door_no', 'Door No')}
          {permanentField('permanent_street', 'Street')}
          {permanentField('permanent_village', 'Village / Town')}
        </Row>
        <Row>
          {permanentField('permanent_taluk', 'Taluk')}
          {permanentField('permanent_pincode', 'Pincode')}
        </Row>
        <Row>
          {permanentField('permanent_district', 'District')}
          {permanentField('permanent_state', 'State')}
        </Row>
      </div>

      <div className="border-top pt-3 mt-2">
        <Row>
          <Col md={6}>
            <FileUploadField
              label="Address Proof"
              value={data.address_proof || ''}
              onChange={(v) => set('address_proof', v)}
              error={errors.address_proof}
              helpText="Utility bill, ration card, or rental agreement"
            />
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default Step3Address;